import React, { Suspense, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { selectLanguage } from "@/features/config/configSlice";
import { selectSpeakAloudIndications } from "@/features/navigation/navigationSlice.selectors";

import { useAppSelector } from "@/app/hooks";
import Modal from "@/features/app/components/modals/Modal";
import { FALLBACK_LANGUAGE } from "@/features/app/constants";
import type { SupportedLanguage } from "@/features/config/types";
import { speakIndication } from "@/features/navigation/actions";
import { isIosSafari } from "@/features/utils/utils";

import "./action-modal.scss";

const TTS_TIMEOUT = 2500;

export const ActionsCustomizableModal = ({
  title,
  description,
  mobileFullscreen = false,
}: {
  title?: { [key in SupportedLanguage]?: string };
  description?: { [key in SupportedLanguage]?: string };
  mobileFullscreen?: boolean;
}) => {
  const { t } = useTranslation();
  const speakAloudIndications = useAppSelector(selectSpeakAloudIndications);
  const lang = useAppSelector(selectLanguage);

  const [isOpen, setisOpen] = useState(true);
  const [blockScreenReader, setBlockScreenReader] = useState(
    speakAloudIndications,
  );

  const currentTitle = title[lang] || title[FALLBACK_LANGUAGE] || "";
  const currentDescription =
    description[lang] || description[FALLBACK_LANGUAGE] || "";

  const shouldBlockScreenReader = speakAloudIndications && blockScreenReader;

  const isIOS = useMemo(() => {
    return isIosSafari();
  }, []);

  useEffect(() => {
    if (!speakAloudIndications || !isOpen) {
      return;
    }
    const div = document.createElement("div");
    // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
    div.innerHTML = currentTitle + "\n" + currentDescription;
    const text = div.textContent || div.innerText || "";

    setTimeout(() => {
      speakIndication({ onEnd: () => setBlockScreenReader(false), text });
      if (!isIOS) {
        setBlockScreenReader(false);
      }
    }, TTS_TIMEOUT);
  }, [isOpen]);

  // Make sure only modal can be accesed by talkback
  useEffect(() => {
    const rootElement = document.getElementById("root");
    const bottomDrawerOverlayElement = document.querySelector(
      '[data-rsbs-overlay="true"]',
    ) as HTMLElement;
    rootElement?.setAttribute("aria-hidden", `${isOpen}`);
    bottomDrawerOverlayElement?.setAttribute("aria-hidden", `${isOpen}`);
  }, [isOpen]);

  if (!currentTitle && !currentDescription) {
    return null;
  }

  return (
    <Suspense>
      <Modal
        wrapperClassName="actions-modal"
        isOpen={isOpen}
        mobileFullscreen={mobileFullscreen}
        // onTapOutsideModal={() => setisOpen(false)}
      >
        {/*This button is focused on iOS whenever the modal is opened and TTS is enabled,
          this allows to minimize the overlap between screen reader and TTS*/}
        {isIOS && shouldBlockScreenReader && (
          <button
            type="button"
            aria-label=" "
            className="sr-button"
            // When the user unfocuses this button, we no longer need it
            onBlur={() => setBlockScreenReader(false)}
          />
        )}
        <Modal.Title ariaHidden={shouldBlockScreenReader}>
          {currentTitle}
        </Modal.Title>

        <Modal.Content ariaHidden={shouldBlockScreenReader}>
          <span
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{
              __html: currentDescription,
            }}
          ></span>
        </Modal.Content>

        <Modal.ButtonsArea>
          <Modal.Button onClick={() => setisOpen(false)}>
            {t("generic.ok")}
          </Modal.Button>
        </Modal.ButtonsArea>
      </Modal>
    </Suspense>
  );
};
